/**
 * /admin/platform/llm/dashboard — usage + cost overview.
 *
 * Reads /v1/admin/platform/llm/dashboard for the configured trailing
 * window (default 30 days) and renders:
 *   - the active primary + fallback ("which model is being used");
 *   - top-line totals (calls, tokens, spend, averages);
 *   - per-model spend table over the window;
 *   - the last twenty model_call rows.
 *
 * Costs are stored as integer micros of AUD per million tokens; we
 * format to A$ with two decimals on the way out.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  adminApi,
  type AdminApiError,
  type LlmDashboard,
  type LlmProviderConfigOut,
} from "./api";

const WINDOW_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 365, label: "365 days" },
];

function formatAud(micros: number): string {
  // 1 AUD = 1,000,000 micros.
  const aud = micros / 1_000_000;
  return `A$${aud.toFixed(aud >= 100 ? 0 : 2)}`;
}

function formatAudFractional(micros: number): string {
  const aud = micros / 1_000_000;
  return `A$${aud.toFixed(4)}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString("en-AU");
}

function formatTime(iso: string): string {
  try {
    const dt = new Date(iso);
    return dt.toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function ActiveProviderCell({
  label,
  config,
}: {
  label: string;
  config: LlmProviderConfigOut | null;
}) {
  if (!config) {
    return (
      <div className="kpi">
        <div className="kpi-label">{label}</div>
        <div className="kpi-value kpi-muted">Not configured</div>
      </div>
    );
  }
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{config.model_id}</div>
      <div className="kpi-sub">
        {config.provider} · {config.enabled ? "enabled" : "disabled"}
      </div>
    </div>
  );
}

export function LlmDashboardPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<LlmDashboard | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load(window: number) {
    setLoaded(false);
    try {
      const result = await adminApi.llmDashboard(window);
      setData(result);
      setError(null);
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      setError(apiErr.message ?? "Could not load dashboard.");
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    void load(days);
  }, [days]);

  return (
    <div className="admin-page">
      <header className="admin-page-head admin-page-head-row">
        <div>
          <h2>LLM dashboard</h2>
          <p className="admin-page-blurb">
            Aggregated usage and spend for the trailing window. Costs are
            computed at the time of each call from the rate sheet, so editing
            rates does not retroactively rewrite history.
          </p>
        </div>
        <div className="field">
          <label htmlFor="dashboard-window">Window</label>
          <select
            id="dashboard-window"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {WINDOW_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </header>

      {error !== null && <div className="banner banner-error">{error}</div>}
      {!loaded && <div>Loading…</div>}

      {data !== null && data.models_missing_rates.length > 0 && (
        <div className="banner banner-warn">
          <strong>Spend reads as zero for some calls.</strong> The following
          models have calls in this window but no entry in the rate sheet, so
          their cost was recorded as zero at call time:{" "}
          {data.models_missing_rates.join(", ")}.{" "}
          <Link
            to={`/admin/platform/llm/rates?missing=${encodeURIComponent(
              data.models_missing_rates.join(","),
            )}`}
          >
            Add rates
          </Link>{" "}
          to fix future calls. (History is not rewritten.)
        </div>
      )}

      {data !== null && (
        <>
          <div className="kpi-grid">
            <ActiveProviderCell label="Active primary" config={data.active_primary} />
            <ActiveProviderCell
              label="Active fallback"
              config={data.active_fallback}
            />
            <div className="kpi">
              <div className="kpi-label">Total spend ({days}d)</div>
              <div className="kpi-value">
                {formatAud(data.total_cost_aud_micros)}
              </div>
              <div className="kpi-sub">{formatNumber(data.total_calls)} calls</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Tokens ({days}d)</div>
              <div className="kpi-value">
                {formatNumber(
                  data.total_input_tokens + data.total_output_tokens,
                )}
              </div>
              <div className="kpi-sub">
                in: {formatNumber(data.total_input_tokens)} · out:{" "}
                {formatNumber(data.total_output_tokens)}
              </div>
            </div>
          </div>

          <h3 className="admin-section-title">Averages per call</h3>
          <div className="kpi-grid kpi-grid-quad">
            <div className="kpi">
              <div className="kpi-label">Avg input tokens</div>
              <div className="kpi-value">
                {Math.round(data.average_input_tokens).toLocaleString("en-AU")}
              </div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Avg output tokens</div>
              <div className="kpi-value">
                {Math.round(data.average_output_tokens).toLocaleString("en-AU")}
              </div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Avg latency</div>
              <div className="kpi-value">
                {Math.round(data.average_latency_ms).toLocaleString("en-AU")} ms
              </div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Avg cost</div>
              <div className="kpi-value">
                {formatAudFractional(data.average_cost_aud_micros)}
              </div>
            </div>
          </div>

          <h3 className="admin-section-title">Spend by model</h3>
          {data.spend_by_model.length === 0 ? (
            <div className="empty-row">
              No calls recorded in this window.
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Calls</th>
                  <th>Input tokens</th>
                  <th>Output tokens</th>
                  <th>Spend</th>
                </tr>
              </thead>
              <tbody>
                {data.spend_by_model.map((row) => (
                  <tr key={row.model}>
                    <td>{row.model}</td>
                    <td>{formatNumber(row.calls)}</td>
                    <td>{formatNumber(row.input_tokens)}</td>
                    <td>{formatNumber(row.output_tokens)}</td>
                    <td>{formatAud(row.cost_aud_micros)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 className="admin-section-title">Recent calls</h3>
          {data.recent_calls.length === 0 ? (
            <div className="empty-row">
              No calls recorded in this window.
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Model</th>
                  <th>Input</th>
                  <th>Output</th>
                  <th>Latency</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_calls.map((call, idx) => (
                  <tr key={`${call.occurred_at}-${idx}`}>
                    <td>{formatTime(call.occurred_at)}</td>
                    <td>{call.model}</td>
                    <td>{formatNumber(call.input_tokens)}</td>
                    <td>{formatNumber(call.output_tokens)}</td>
                    <td>{call.latency_ms} ms</td>
                    <td>{formatAudFractional(call.cost_aud_micros)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
